import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { CustomTable } from "../../componentes/table/CustomTable";
import Button from "../../componentes/button/button";
import StatusBadge from "../../components/solicitacoes/Shared/StatusBadge";
import FiltroSolicitacao from "../../components/solicitacoes/Shared/FiltroSolicitacao";
import AlterarResponsavelSidebar from "../../components/solicitacoes/Shared/AlterarResponsavelSidebar";
import EliminarSolicitacaoModal from "../../components/solicitacoes/Shared/EliminarSolicitacaoModal";
import { useGerenciarSolicitacoes } from "../../hooks/solicitacoes/useGerenciarSolicitacoes";
import { useFiltrosPersistidos } from "../../hooks/solicitacoes/useFiltrosPersistidos";
import SolicitacaoAdminService from "../../services/resource/solicitacaoAdminService";
import { processoInfo, etapaLabel } from "../../utils/solicitacoes/processoRegistry";
import { humanizarEtapa } from "../../utils/solicitacoes/statusConfig";
import { primeiroDiaMesAtualIso, hojeIso } from "../../utils/solicitacoes/datas";

const service = new SolicitacaoAdminService();

const CAMPOS_FILTRO = ["tipoProcesso", "status", "numero", "solicitanteId", "responsavelId", "etapaCodigo", "grupoId"];

const FILTROS_INICIAIS = {
    tipoProcesso: null, status: null, numero: "", solicitanteId: null, responsavelId: null,
    etapaCodigo: null, grupoId: null, dataInicial: primeiroDiaMesAtualIso(), dataFinal: hojeIso(),
};

export default function GerenciarSolicitacoes() {
    const [filtros, setFiltros, limparFiltros] = useFiltrosPersistidos("solicitacoes.gerenciar.filtros", FILTROS_INICIAIS);

    useEffect(() => {
        if (!filtros.dataInicial || !filtros.dataFinal) {
            setFiltros(prev => ({
                ...prev,
                dataInicial: prev.dataInicial || FILTROS_INICIAIS.dataInicial,
                dataFinal: prev.dataFinal || FILTROS_INICIAIS.dataFinal,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [filtrosAplicados, setFiltrosAplicados] = useState(() => ({
        tipoProcesso: filtros.tipoProcesso,
        status: filtros.status,
        dataInicial: filtros.dataInicial || FILTROS_INICIAIS.dataInicial,
        dataFinal: filtros.dataFinal || FILTROS_INICIAIS.dataFinal,
    }));

    useEffect(() => {
        setFiltrosAplicados(prev => ({ ...prev, tipoProcesso: filtros.tipoProcesso, status: filtros.status }));
    }, [filtros.tipoProcesso, filtros.status]);

    const { solicitacoes, loading, recarregar } = useGerenciarSolicitacoes(
        filtrosAplicados.tipoProcesso, filtrosAplicados.status, filtrosAplicados.dataInicial, filtrosAplicados.dataFinal
    );

    useEffect(() => {
        document.title = "MyDash | Gerenciar Solicitações";
    }, []);

    function pesquisar() {
        if (!filtros.dataInicial || !filtros.dataFinal) {
            toast.error("Informe a data inicial e a data final para realizar a consulta.");
            return;
        }
        if (filtros.dataInicial > filtros.dataFinal) {
            toast.error("A data inicial não pode ser maior que a data final.");
            return;
        }
        setFiltrosAplicados({
            tipoProcesso: filtros.tipoProcesso, status: filtros.status,
            dataInicial: filtros.dataInicial, dataFinal: filtros.dataFinal,
        });
    }

    const solicitacoesFiltradas = useMemo(() => {
        return solicitacoes.filter(s => {
            if (filtros.numero && !s.numero?.toLowerCase().includes(filtros.numero.toLowerCase())) return false;
            if (filtros.solicitanteId && s.solicitanteId !== filtros.solicitanteId) return false;
            if (filtros.responsavelId && s.responsavelAtualId !== filtros.responsavelId) return false;
            if (filtros.etapaCodigo && s.etapaAtual !== filtros.etapaCodigo) return false;
            if (filtros.grupoId && String(s.grupoResponsavelAtualId) !== String(filtros.grupoId)) return false;
            return true;
        });
    }, [solicitacoes, filtros.numero, filtros.solicitanteId, filtros.responsavelId, filtros.etapaCodigo, filtros.grupoId]);

    const [alvoResponsavel, setAlvoResponsavel] = useState(null);
    const [alvoEliminar, setAlvoEliminar] = useState(null);

    function confirmarAlterarResponsavel(novoUsuarioId, motivo) {
        return service.alterarResponsavel(alvoResponsavel.id, novoUsuarioId, motivo)
            .then(() => {
                toast.success("Responsável alterado com sucesso!");
                setAlvoResponsavel(null);
                recarregar();
            })
            .catch(error => {
                console.error("Erro ao alterar responsável", error);
                toast.error("Erro ao alterar responsável");
                throw error;
            });
    }

    function confirmarEliminar(motivo) {
        return service.eliminar(alvoEliminar.id, motivo)
            .then(() => {
                toast.success("Solicitação eliminada com sucesso!");
                setAlvoEliminar(null);
                recarregar();
            })
            .catch(error => {
                console.error("Erro ao eliminar solicitação", error);
                toast.error("Erro ao eliminar solicitação");
                throw error;
            });
    }

    const linhas = useMemo(() => solicitacoesFiltradas.map(s => ({
        ...s,
        processoLabel: processoInfo(s.tipoProcesso)?.label || s.tipoProcesso,
        etapaLabelTxt: s.etapaAtual ? (etapaLabel(s.tipoProcesso, s.etapaAtual) || humanizarEtapa(s.etapaAtual)) : "Encerrada",
        responsavel: s.responsavelAtualNome || "-",
        grupo: s.grupoResponsavelAtualNome || "-",
    })), [solicitacoesFiltradas]);

    const colunas = [
        { key: "numero", label: "Número", width: 120 },
        { key: "processoLabel", label: "Processo" },
        { key: "solicitanteNome", label: "Solicitante" },
        { key: "etapaLabelTxt", label: "Etapa" },
        { key: "responsavel", label: "Responsável" },
        { key: "grupo", label: "Grupo atual" },
        { key: "status", label: "Situação", render: v => <StatusBadge status={v} />, center: true },
        { key: "dataCriacao", label: "Criada em", format: "date" },
        { key: "dataAtualizacao", label: "Atualizada em", format: "date" },
        {
            key: "acoes", label: "Ações", sortable: false, filterable: false, center: true, noSelected: true,
            render: (_, row) => (
                <div className="flex items-center justify-center gap-2">
                    <i
                        title="Alterar responsável"
                        onClick={() => setAlvoResponsavel(row)}
                        className="fa fa-user-edit text-primary hover:text-primary/80 p-1.5 rounded-full hover:bg-primary/10 cursor-pointer"
                    />
                    <i
                        title="Eliminar solicitação"
                        onClick={() => setAlvoEliminar(row)}
                        className="fa fa-trash text-destructive hover:text-destructive/80 p-1.5 rounded-full hover:bg-destructive/10 cursor-pointer"
                    />
                </div>
            ),
        },
    ];

    return (
        <main className="px-4 py-6">
            <div className="mx-auto flex flex-col gap-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <h1 className="font-display text-2xl font-bold text-foreground">Gerenciar Solicitações</h1>
                </div>

                <div className="bg-card border border-border rounded-lg shadow-card p-4">
                    <div className="flex items-end gap-3 flex-wrap mb-4 pb-4 border-b border-border">
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground/80">
                                Data Inicial <span className="text-destructive">*</span>
                            </label>
                            <input
                                type="date"
                                value={filtros.dataInicial ?? ""}
                                onChange={e => setFiltros({ ...filtros, dataInicial: e.target.value })}
                                className="h-9 px-3 text-sm border border-input rounded-lg bg-card outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground/80">
                                Data Final <span className="text-destructive">*</span>
                            </label>
                            <input
                                type="date"
                                value={filtros.dataFinal ?? ""}
                                onChange={e => setFiltros({ ...filtros, dataFinal: e.target.value })}
                                className="h-9 px-3 text-sm border border-input rounded-lg bg-card outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
                            />
                        </div>
                        <Button variant="blue" label="Pesquisar" iconLeft="fa fa-search" loading={loading} onClick={pesquisar} />
                    </div>

                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-sm font-semibold text-foreground">Filtros</h2>
                        <button onClick={limparFiltros} className="text-xs text-muted-foreground hover:text-foreground hover:underline">
                            Limpar filtros
                        </button>
                    </div>
                    <FiltroSolicitacao value={filtros} onChange={setFiltros} campos={CAMPOS_FILTRO} />
                </div>

                <div className="bg-card border border-border rounded-lg shadow-card">
                    <CustomTable
                        data={linhas}
                        columns={colunas}
                        loading={loading}
                        showHeader={false}
                        className="text-xs"
                        pagination
                        pageSize={20}
                    />
                </div>
            </div>

            <AlterarResponsavelSidebar
                isOpen={!!alvoResponsavel}
                solicitacao={alvoResponsavel}
                onFechar={() => setAlvoResponsavel(null)}
                onConfirmar={confirmarAlterarResponsavel}
            />

            <EliminarSolicitacaoModal
                visible={!!alvoEliminar}
                solicitacao={alvoEliminar}
                onFechar={() => setAlvoEliminar(null)}
                onConfirmar={confirmarEliminar}
            />
        </main>
    );
}
